/* eslint-disable promise/catch-or-return */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Flex,
  Select,
  Separator,
  Skeleton,
  Text,
  toast,
  useTheme,
  SunIcon,
  MoonIcon,
  MonitorIcon,
  THEMES,
} from '@buffer-mono/popcorn'
import type { Theme } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useSplitEnabled } from '@buffer-mono/features'

import { TimeZoneSelect } from '~publish/components/TimeZoneSelect'
import {
  useAccount,
  useAccountId,
  useAccountPreferences,
  useAccountTimezone,
} from '~publish/contexts/AccountContext'
import type { VariablesOf } from '~publish/graphql'
import { BUFFER_HOME_ANNOUNCEMENT_BANNER_ID } from '~publish/components/MainSidebar/components/BufferHomeAnnouncementCoachmark'
import { useDefaultLandingPage } from '~publish/hooks/useDefaultLandingPage'
import { useDefaultScheduleOption } from '~publish/hooks/useDefaultScheduleOption'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { useHasAccessToBufferHome } from '~publish/hooks/useHasAccessToBufferHome'
import { useTimeFormatPreference } from '~publish/hooks/useTimeFormatPreference'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { SCHEDULE_OPTIONS } from '~publish/legacy/composer/composer/components/composer-footer/schedule-selector/constants'
import { getDebugLogger } from '~publish/legacy/utils/getDebugLogger'

import type { UpdateAccountPreferences } from './hooks/useUpdateAccountPreferences'
import { useUpdateAccountPreferences } from './hooks/useUpdateAccountPreferences'
import { useUpdateTimeZonePreferences } from './hooks/useUpdateTimeZonePreferences'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsRow } from '../shared/components/SettingsRow'

import styles from './Preferences.module.css'

// Preference enums derived from the update mutation's input, so the values
// asserted below stay in sync with what the mutation accepts.
type UpdateAccountPreferencesInput = VariablesOf<
  typeof UpdateAccountPreferences
>['input']
type TimeFormatEnum = NonNullable<UpdateAccountPreferencesInput['timeFormat']>
type StartOfWeekEnum = NonNullable<UpdateAccountPreferencesInput['startOfWeek']>
type ScheduleOption = NonNullable<
  UpdateAccountPreferencesInput['defaultScheduleOption']
>
type LandingPage = NonNullable<UpdateAccountPreferencesInput['landingPage']>
type MentionsCounterDisplay = NonNullable<
  UpdateAccountPreferencesInput['mentionsCounter']
>

const debugLog = getDebugLogger('UpdateAccountPreferences')

const genericSuccessMessage = 'Great! Your preferences have been saved'

function assertValidTheme(value: string): asserts value is Theme {
  if (!Object.hasOwn(THEMES, value)) {
    throw new Error(`Invalid theme: ${value}`)
  }
}

const ThemeAppearance = (): JSX.Element => {
  const { theme, setTheme } = useTheme()
  const accountId = useAccountId()

  const themeOptions = [
    {
      value: 'light' as const,
      label: 'Light',
      icon: <SunIcon aria-hidden="true" />,
    },
    {
      value: 'dark' as const,
      label: 'Dark',
      icon: <MoonIcon aria-hidden="true" />,
    },
    {
      value: 'system' as const,
      label: 'System',
      icon: <MonitorIcon aria-hidden="true" />,
    },
  ]

  const selectedOption = themeOptions.find((option) => option.value === theme)
  const themeLabel = selectedOption?.label || 'Light'

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidTheme(value)
      if (value !== theme) {
        setTheme(value)
        toast.success(genericSuccessMessage)

        if (accountId) {
          BufferTracker.themeUpdated({
            accountId,
            theme: value,
            previousTheme: theme,
            source: 'settings',
          })
        }
      }
    },
    [setTheme, theme, accountId],
  )

  return (
    <SettingsRow id="appearance" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Appearance</SettingsRow.Title>
        <SettingsRow.Description>
          Customize the look and feel of Buffer on this device.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Select onValueChange={handleOnChange} value={theme}>
            <Select.Trigger
              id="appearance"
              aria-labelledby={ids.headingId}
              aria-describedby={ids.descriptionId}
              placeholder={themeLabel}
              size="medium"
            />
            <Select.Content
              position="popper"
              align="end"
              sideOffset={0}
              className={styles.selectContent}
            >
              {themeOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  <Flex gap="sm" align="center">
                    {option.icon}
                    <Text weight="medium">{option.label}</Text>
                  </Flex>
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const TimeZone = ({
  isLoading,
  timezone,
}: {
  isLoading: boolean
  timezone: string
}): JSX.Element => {
  const [localTimezone, setLocalTimezone] = useState(timezone)
  const {
    updateTimeZone,
    isLoading: isUpdatingTimeZone,
    hasError,
  } = useUpdateTimeZonePreferences()

  useEffect(() => {
    setLocalTimezone(timezone)
  }, [timezone])

  const handleOnChange = useCallback(
    (value: string): void => {
      if (value && value !== localTimezone) {
        setLocalTimezone(value)
        updateTimeZone({ timeZone: value }).then(() => {
          toast.success(genericSuccessMessage)
          return undefined
        })
      }
    },
    [updateTimeZone, localTimezone],
  )

  useEffect(() => {
    if (hasError) {
      setLocalTimezone(timezone) // Reset to previous value on error
      toast.error(
        'Sorry! There was a problem updating your selected timezone. Please try again.',
      )
    }
  }, [hasError, timezone])

  return (
    <SettingsRow id="timezone" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Timezone</SettingsRow.Title>
        <SettingsRow.Description>
          Used as the default timezone for new connected channels and for
          sending email notifications. Also used for calculating your posting
          streaks.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Skeleton show={isLoading} data-testid="timezone-skeleton">
            <TimeZoneSelect
              variant="secondary"
              size="medium"
              onChange={handleOnChange}
              value={localTimezone}
              disabled={isUpdatingTimeZone}
              aria-describedby={ids.descriptionId}
            />
          </Skeleton>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

function assertTimeFormat(value: string): asserts value is TimeFormatEnum {
  if (!['twelveHour', 'twentyFourHour'].includes(value)) {
    throw new Error('Invalid time format')
  }
}

const TimeFormat = ({ isLoading }: { isLoading: boolean }): JSX.Element => {
  const timeFormat =
    useTimeFormatPreference() === '12' ? 'twelveHour' : 'twentyFourHour'
  const timeFormatOptions = [
    { label: '12-hour', value: 'twelveHour', timeFormat: '12' },
    { label: '24-hour', value: 'twentyFourHour', timeFormat: '24' },
  ]

  const selectedOption = timeFormatOptions.find(
    (option) => option.timeFormat === timeFormat,
  )

  const timeFormatLabel = selectedOption?.label || '12-hour'

  const [updateAccountPreferences, { loading, error }] =
    useUpdateAccountPreferences()

  const handleOnChange = useCallback(
    (value: string): void => {
      assertTimeFormat(value)
      updateAccountPreferences({
        variables: {
          input: {
            timeFormat: value,
          },
        },
      }).then(({ success }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your selected time format. Please try again.',
          )
        }
        return undefined
      })
    },
    [updateAccountPreferences, error],
  )

  return (
    <SettingsRow id="time-format" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Time Format</SettingsRow.Title>
        <SettingsRow.Description>
          Set the time format for the Calendar and Queue.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Skeleton show={isLoading} data-testid="time-format-skeleton">
            <Select onValueChange={handleOnChange} value={timeFormat}>
              <Select.Trigger
                id="time-format"
                aria-labelledby={ids.headingId}
                aria-describedby={ids.descriptionId}
                placeholder={timeFormatLabel}
                disabled={loading}
                size="medium"
              />
              <Select.Content
                position="popper"
                align="end"
                sideOffset={0}
                className={styles.selectContent}
              >
                {timeFormatOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Skeleton>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

function assertValidStartOfWeek(
  value: string,
): asserts value is StartOfWeekEnum {
  if (!['monday', 'sunday'].includes(value)) {
    throw new Error('Invalid start of week')
  }
}

const StartOfWeek = ({ isLoading }: { isLoading: boolean }): JSX.Element => {
  const startOfWeek = useWeekStartsOn()
  const [updateAccountPreferences, { loading, error }] =
    useUpdateAccountPreferences()

  const startOfWeekOptions = [
    { label: 'Monday', value: 'monday' },
    { label: 'Sunday', value: 'sunday' },
  ]

  const selectedOption = startOfWeekOptions.find(
    (option) => option.value === startOfWeek,
  )
  const startOfWeekLabel = selectedOption?.label || 'Monday'

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidStartOfWeek(value)
      updateAccountPreferences({
        variables: {
          input: {
            startOfWeek: value,
          },
        },
      }).then(({ success }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your selected start of week. Please try again.',
          )
        }
        return undefined
      })
    },
    [updateAccountPreferences, error],
  )

  return (
    <SettingsRow id="start-of-week" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Start of Week</SettingsRow.Title>
        <SettingsRow.Description>
          Set the first day of the week for the Calendar, date picker, and your
          posting streaks.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Skeleton show={isLoading} data-testid="start-of-week-skeleton">
            <Select onValueChange={handleOnChange} value={startOfWeek}>
              <Select.Trigger
                id="start-of-week"
                aria-labelledby={ids.headingId}
                aria-describedby={ids.descriptionId}
                placeholder={startOfWeekLabel}
                disabled={loading}
                size="medium"
              />
              <Select.Content
                position="popper"
                align="end"
                sideOffset={0}
                className={styles.selectContent}
              >
                {startOfWeekOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Skeleton>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const LANDING_PAGE_OPTIONS: { value: LandingPage; label: string }[] = [
  { value: 'home', label: 'Buffer Home' },
  { value: 'default', label: 'Schedule' },
]

function assertValidLandingPage(value: string): asserts value is LandingPage {
  if (!LANDING_PAGE_OPTIONS.some((option) => value === option.value)) {
    throw new Error('Invalid landing page')
  }
}

const DefaultLandingPage = (): JSX.Element => {
  const landingPage = useDefaultLandingPage()
  const [updateAccountPreferences, { loading }] = useUpdateAccountPreferences()
  const { dismiss: dismissBufferHomeCoachmark } = useDismissableBanner(
    BUFFER_HOME_ANNOUNCEMENT_BANNER_ID,
  )

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidLandingPage(value)
      // Choosing Schedule (`'default'`) is an explicit decision against Buffer
      // Home — `'default'` is indistinguishable from "never opted in", so
      // suppress the announcement coachmark by dismissing it for this user.
      if (value === 'default') {
        dismissBufferHomeCoachmark()
      }
      updateAccountPreferences({
        variables: {
          input: {
            landingPage: value,
          },
        },
      }).then(({ success, error }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your landing page. Please try again.',
          )
        }
        return undefined
      })
    },
    [updateAccountPreferences, dismissBufferHomeCoachmark],
  )

  const selectedLabel =
    LANDING_PAGE_OPTIONS.find((option) => option.value === landingPage)
      ?.label ?? 'Schedule'

  return (
    <SettingsRow id="default-landing-page" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Landing Page</SettingsRow.Title>
        <SettingsRow.Description>
          Choose where you land after logging in.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Select onValueChange={handleOnChange} value={landingPage}>
            <Select.Trigger
              id="default-landing-page"
              aria-labelledby={ids.headingId}
              aria-describedby={ids.descriptionId}
              placeholder={selectedLabel}
              disabled={loading}
              size="medium"
            />
            <Select.Content
              position="popper"
              align="end"
              sideOffset={0}
              className={styles.selectContent}
            >
              {LANDING_PAGE_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const ScheduleOptionKeys: ScheduleOption[] = [
  'Queue',
  'Now',
  'FixedTime',
  'Prioritize',
]

function assertValidOption(value: string): asserts value is ScheduleOption {
  if (!ScheduleOptionKeys.some((option) => value === option)) {
    throw new Error('Invalid schedule option')
  }
}

const DefaultScheduleOption = ({
  isLoading,
}: {
  isLoading: boolean
}): JSX.Element => {
  const defaultScheduleOption = useDefaultScheduleOption()

  const [updateAccountPreferences, { loading }] = useUpdateAccountPreferences()

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidOption(value)
      updateAccountPreferences({
        variables: {
          input: {
            defaultScheduleOption: value,
          },
        },
      }).then(({ success, error }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your default posting action. Please try again.',
          )
        }
        return undefined
      })
    },
    [updateAccountPreferences],
  )

  return (
    <SettingsRow id="default-schedule-option" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Default Posting Action</SettingsRow.Title>
        <SettingsRow.Description>
          Set a default time to post for the composer. We will use it whenever
          the composer is triggered without a specific time.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Skeleton
            show={isLoading}
            data-testid="default-schedule-option-skeleton"
          >
            <Select
              onValueChange={handleOnChange}
              value={defaultScheduleOption}
            >
              <Select.Trigger
                id="default-schedule-option"
                aria-labelledby={ids.headingId}
                aria-describedby={ids.descriptionId}
                placeholder={SCHEDULE_OPTIONS[defaultScheduleOption].label}
                disabled={loading}
                size="medium"
              />
              <Select.Content
                position="popper"
                align="end"
                sideOffset={0}
                className={styles.selectContent}
              >
                {ScheduleOptionKeys.map((option) => (
                  <Select.Item key={option} value={option}>
                    {SCHEDULE_OPTIONS[option].label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Skeleton>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const MENTIONS_COUNTER_OPTIONS: {
  value: MentionsCounterDisplay
  label: string
  description: string
}[] = [
  {
    value: 'counter',
    label: 'Counter',
    description: 'Show a full count of unanswered mentions',
  },
  {
    value: 'dot',
    label: 'Dot',
    description: 'Indicate new mentions since your last visit',
  },
]

function assertValidMentionsCounter(
  value: string,
): asserts value is MentionsCounterDisplay {
  if (!MENTIONS_COUNTER_OPTIONS.some((option) => value === option.value)) {
    throw new Error('Invalid mentions counter option')
  }
}

const MentionsCounter = (): JSX.Element => {
  const preferences = useAccountPreferences()
  const mentionsCounter = preferences?.community?.mentionsCounter ?? 'counter'
  const [updateAccountPreferences, { loading }] = useUpdateAccountPreferences()

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidMentionsCounter(value)
      updateAccountPreferences({
        variables: {
          input: {
            mentionsCounter: value,
          },
        },
      }).then(({ success, error }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your mentions counter. Please try again.',
          )
        }
        return undefined
      })
    },
    [updateAccountPreferences],
  )

  const selectedLabel =
    MENTIONS_COUNTER_OPTIONS.find((option) => option.value === mentionsCounter)
      ?.label ?? 'Counter'

  return (
    <SettingsRow id="mentions-counter" className={styles.settingRow}>
      <SettingsRow.Header>
        <SettingsRow.Title>Mentions Counter</SettingsRow.Title>
        <SettingsRow.Description>
          Choose how new mentions are shown in Community. Applies to all
          channels.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Select onValueChange={handleOnChange} value={mentionsCounter}>
            <Select.Trigger
              id="mentions-counter"
              aria-labelledby={ids.headingId}
              aria-describedby={ids.descriptionId}
              placeholder={selectedLabel}
              disabled={loading}
              size="medium"
            />
            <Select.Content
              position="popper"
              align="end"
              sideOffset={0}
              className={styles.selectContent}
            >
              {MENTIONS_COUNTER_OPTIONS.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  description={option.description}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const Preferences = (): JSX.Element => {
  const { loading } = useAccount()
  const timezone = useAccountTimezone()
  const { hasAccess: hasAccessToHome } = useHasAccessToBufferHome()
  const { isEnabled: isMentionsEnabled, isReady: isMentionsReady } =
    useSplitEnabled('geid-cet-mentions-tab')

  return (
    <NewSettingsPageLayout title="Preferences" gap="xl">
      <ThemeAppearance />
      <Separator className={styles.separator} />
      <TimeZone isLoading={loading} timezone={timezone} />
      <Separator className={styles.separator} />
      <TimeFormat isLoading={loading} />
      <Separator className={styles.separator} />
      <StartOfWeek isLoading={loading} />
      {hasAccessToHome && (
        <>
          <Separator className={styles.separator} />
          <DefaultLandingPage />
        </>
      )}
      <Separator className={styles.separator} />
      <DefaultScheduleOption isLoading={loading} />
      {isMentionsReady && isMentionsEnabled && (
        <>
          <Separator className={styles.separator} />
          <MentionsCounter />
        </>
      )}
    </NewSettingsPageLayout>
  )
}

export default Preferences
